import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import config from "../../config";
import { useLanguage } from "../../Context";
import { IoMdEye } from "react-icons/io";

export default function VideoView() {
  const [items, setItems] = useState(null);
  const [relatedContents, setRelatedContents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isExpanded, setIsExpanded] = useState(false);

  const { subcategory, id } = useParams();

  const { language } = useLanguage();

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    fetch(
      `${config.apiUrl}contentapi.php?option=1&type=6&subcategory=${subcategory}&id=${id}`
    )
      .then((response) => response.json())
      .then((items) => {
        setItems(items);
        setIsLoading(false);
      })
      .catch((error) => {
        setError(error);
        setIsLoading(false);
      });
  }, [subcategory, id]);

  const fetchRelatedContents = () => {
    fetch(
      `${config.apiUrl}contentapi.php?option=1&type=7&categoryid=1&subcategory=${subcategory}&limitCount=6&id=${id}&lang=${language}`
    )
      .then((response) => response.json())
      .then((relatedContents) => setRelatedContents(relatedContents))
      .catch((error) => console.error("Error fetching data", error));
  };

  useEffect(() => {
    fetchRelatedContents();
  }, [subcategory, id]);

  if (isLoading) {
    return (
      <div className="max-container-width pt-4 pb-3">
        <div className="placeholder col-3 mb-2" style={{ height: "2.5rem" }} />
        <div className="row placeholder-glow">
          <div className="col-md-7">
            <div className="h-100">
              <div
                className="placeholder col-12 mb-2"
                style={{ height: "430px" }}
              />
              <div
                className="placeholder col-9 mb-2"
                style={{ height: "2.5rem" }}
              />
              <div className="bg-secondary-subtle p-3">
                <div className="placeholder col-12" />
                <div className="placeholder col-9" />
              </div>
            </div>
          </div>
          <div
            style={{ minHeight: "650px" }}
            className="col-md-5 bg-secondary-subtle p-3"
          >
            <div
              className="placeholder col-5 mb-3"
              style={{ height: "1.5rem" }}
            />
            {[...Array(6)].map((_, index) => (
              <div key={index} className="d-flex justify-content-between mb-3">
                <div className="col-3 placeholder" style={{ height: "5rem" }} />
                <div className="col-9 h-100 p-3">
                  <div className="placeholder col-12" />
                  <div className="placeholder col-9" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // error if data is not fetched
  if (error) {
    return (
      <div className="max-container-width pt-4 pb-3">
        <p className="alert alert-danger" role="alert" dir="ltr">
          Something went wrong: {error.message}
        </p>
      </div>
    );
  }

  const toggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  const video = items.map((video) => {
    return (
      <div key={video.id} className="h-100">
        {video.YoutubeUrl ? (
          <iframe
            className="w-100"
            style={{ height: "430px" }}
            src={`https://www.youtube.com/embed/${video.YoutubeUrl}?autoplay=1`}
            title={video.Title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        ) : (
          <video controls autoPlay className="w-100">
            <source src={video.FileUrl} type="video/mp4" />
            <source src={video.FileUrl} type="video/webm" />
            Your browser does not support the video tag.
          </video>
        )}
        <h4 className="py-2">{video.Title}</h4>
        {video.ShortDesc && (
          <p className="bg-secondary-subtle text-right p-3">
            {isExpanded
              ? video.ShortDesc
              : `${video.ShortDesc.substring(0, 100)}...`}
            <button
              onClick={toggleDescription}
              className="btn btn-link p-0 ms-2"
              style={{ fontSize: "0.9rem" }}
            >
              {isExpanded ? "Show less" : "Show more"}
            </button>
          </p>
        )}
      </div>
    );
  });

  /* mapping over youtube links array located in data.js and producing each playlist element */
  const playlistVideo = relatedContents.map((link) => {
    return (
      <Link
        to={`/video/${link.SubCategory}/${link.id}`}
        key={link.id}
        className="d-flex justify-content-between align-items-center text-decoration-none text-dark primary-text-blue"
        style={{ height: "100px" }}
      >
        {/* video thumbnails column */}
        <div className="col-3">
          <div
            className="position-relative small--thumbnails"
            style={{ width: "100%", height: "100%" }}
          >
            <div className="ratio ratio-16x9">
              <img
                src={link.ImageUrl?.replace(
                  /_large(?=\.(jpg|jpeg|png)$)/,
                  "_thumb"
                )}
                alt="video thumbnails"
                className="w-100 h-100 object-fit-cover"
              />
              <img
                src="/img/arrow-w.png"
                alt="play arrow"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  height: "2rem",
                  width: "2rem",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              />
            </div>
          </div>
        </div>
        {/* video details column */}
        <div className="col-9 h-100 p-3 d-flex flex-column justify-content-between primary-text-blue">
          <p style={{ fontSize: ".9rem", marginBottom: "0" }}>{link.Title}</p>
          <div className="d-flex align-items-center">
            <IoMdEye className="ms-1" /> {link.contentViews}
          </div>
        </div>
      </Link>
    );
  });

  return (
    <>
      <div className="max-container-width pt-4 pb-3 primary-text-blue">
        <h3 className="border-end border-5 pe-2" id="secondary-border-yellow">
          نمایش ویدیو
        </h3>
        {/* Two columns of video - first: embeded video, second: playlist */}
        <div className="row">
          <div className="col-md-7">{video}</div>
          <div style={{ minHeight: "650px" }} className="col-md-5">
            <div
              style={{ minHeight: "600px" }}
              className="overflow-auto bg-secondary-subtle p-3"
            >
              <h6>ویدیو های مرتبط</h6>
              {playlistVideo}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
